/**
 * Recuperación cuando la app falla (Req. 3).
 *
 * Origen: Control de Negocio de Comida, donde la app se rompió en el teléfono
 * y quedó atorada.
 *
 * Al estar configurada para PREGUNTAR antes de actualizar (Req. 2.4), el botón
 * de actualizar vive dentro de la propia app. Si el fallo ocurre antes de
 * poder mostrarlo, no queda salida y hay que borrar el ícono, lo que en iOS
 * BORRA TAMBIÉN LOS DATOS. Este módulo evita llegar a ese punto.
 *
 * DISTINCIÓN IMPORTANTE:
 *   Los cachés del Service Worker guardan los ARCHIVOS del programa.
 *   Los datos (entrenamientos, pesajes, comidas, ayunos) viven en IndexedDB.
 *   Borrar los cachés NO borra los datos.
 */

export interface ResultadoRecuperacion {
  exito: boolean;
  mensaje: string;
}

export function hayConexion(): boolean {
  // navigator.onLine es una pista, no una garantía: puede decir true sin
  // internet real. Basta para decidir entre las dos estrategias.
  return typeof navigator === 'undefined' ? true : navigator.onLine !== false;
}

/**
 * Fuerza la versión nueva: actualiza el Service Worker, descarta los archivos
 * guardados y recarga.
 *
 * SOLO debe usarse con conexión. Sin señal, borrar los archivos dejaría la app
 * sin poder abrirse, que es peor que el fallo original (Req. 3.2).
 */
export async function forzarActualizacion(): Promise<ResultadoRecuperacion> {
  if (!hayConexion()) {
    return {
      exito: false,
      mensaje:
        'No hay conexión. Conéctate a internet antes de actualizar: sin señal, ' +
        'descartar los archivos guardados dejaría la app sin poder abrirse.',
    };
  }

  try {
    if ('serviceWorker' in navigator) {
      const registros = await navigator.serviceWorker.getRegistrations();
      for (const registro of registros) {
        try {
          await registro.update();
        } catch {
          // Si no se puede consultar al servidor, se sigue con lo demás.
        }
        // Pide al trabajador en espera que tome el control de inmediato.
        registro.waiting?.postMessage({ type: 'SKIP_WAITING' });
      }
    }

    // Se descartan los archivos del programa para obligar una descarga limpia.
    // Los datos no viven aquí: están en IndexedDB.
    if ('caches' in globalThis) {
      const nombres = await caches.keys();
      await Promise.all(nombres.map((nombre) => caches.delete(nombre)));
    }

    recargar();
    return { exito: true, mensaje: 'Actualizando…' };
  } catch (error) {
    return {
      exito: false,
      mensaje: `No se pudo actualizar: ${error instanceof Error ? error.message : 'error desconocido'}`,
    };
  }
}

/**
 * Recarga sin tocar los archivos guardados: el Service Worker vuelve a servir
 * los mismos. Sirve para fallos pasajeros y funciona sin conexión.
 */
export function recargar(): void {
  window.location.reload();
}

/** Vuelve al inicio de la app, útil cuando falla una pantalla concreta. */
export function irAlInicio(): void {
  window.location.hash = '#/';
  window.location.reload();
}

export interface ResultadoBusqueda {
  hayNueva: boolean;
  mensaje: string;
}

/**
 * Busca una versión nueva a petición del usuario (Req. 2.5).
 *
 * MOTIVO DE EXISTIR: iOS mantiene las apps instaladas suspendidas en memoria.
 * Al reabrirlas desde el ícono no hace una carga real de la página, y el
 * Service Worker puede tardar en revisar si hay algo nuevo. Sin este botón,
 * actualizar depende de que iOS lo decida.
 *
 * Necesita conexión, y lo dice en lugar de fallar en silencio (Req. 2.9).
 */
export async function buscarActualizacion(): Promise<ResultadoBusqueda> {
  if (!('serviceWorker' in navigator)) {
    return {
      hayNueva: false,
      mensaje:
        'Este navegador no maneja versiones instaladas. Recarga la página para ver la más reciente.',
    };
  }

  if (!hayConexion()) {
    return {
      hayNueva: false,
      mensaje:
        'Buscar una versión nueva necesita internet. El resto de la app funciona sin conexión.',
    };
  }

  try {
    const registros = await navigator.serviceWorker.getRegistrations();
    if (registros.length === 0) {
      return {
        hayNueva: false,
        mensaje:
          'La app no está instalada. Agrégala a tu pantalla de inicio desde Safari.',
      };
    }

    // Pregunta al servidor por una versión nueva.
    for (const registro of registros) {
      await registro.update();
    }

    // Tras consultar, se revisa si quedó alguna esperando para tomar el control.
    const actualizados = await navigator.serviceWorker.getRegistrations();
    const enEspera = actualizados.some(
      (r) => r.waiting !== null || r.installing !== null,
    );

    return enEspera
      ? { hayNueva: true, mensaje: 'Hay una versión nueva lista para instalarse.' }
      : { hayNueva: false, mensaje: 'Ya tienes la versión más reciente.' };
  } catch (error) {
    return {
      hayNueva: false,
      mensaje: `No se pudo consultar: ${error instanceof Error ? error.message : 'error desconocido'}`,
    };
  }
}

/** Estado del Service Worker, para mostrarlo en la pantalla de recuperación (Req. 3.4). */
export async function estadoServiceWorker(): Promise<string> {
  if (!('serviceWorker' in navigator)) return 'no disponible';
  try {
    const registros = await navigator.serviceWorker.getRegistrations();
    if (registros.length === 0) return 'sin registrar';
    const registro = registros[0];
    if (registro === undefined) return 'sin registrar';
    if (registro.waiting !== null) return 'hay una versión nueva esperando';
    if (registro.installing !== null) return 'instalando una versión nueva';
    return 'activo y al día';
  } catch {
    return 'no se pudo consultar';
  }
}
